var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom-v5-compat";
import useToast from "../../../hooks/useToast";
import { clone } from "../../../Shared/utils/clone";
import { useOrderFlow } from "../../OrderFlowProvider/OrderFlowContext";
import { GUID, RecipeIngredientReplacementMode, RecipeTypeSelectionMode, } from "../../OrderFlowProvider/OrderFlowTypes";
import { getSubscriptionMeals } from "../../utils/orderFlowUtils";
import { editSubscriptionCalculate, saveOrderData } from "../../utils/requests";
import { getDeliveryDatesForOrder, getProductsForOrderPeriod, mealToCheckoutConverter, } from "./sessionUtils";
export var OrderFlowErrorMessages;
(function (OrderFlowErrorMessages) {
    OrderFlowErrorMessages["DEFAULT"] = "Something unexpected happened. Please start over.";
    OrderFlowErrorMessages["COUPON"] = "Something unexpected happened while applying coupon. Please try again.";
    OrderFlowErrorMessages["REVIEWING_ORDER"] = "Something unexpected happened while reviewing your order. Please start over.";
    OrderFlowErrorMessages["UPDATING_SUBSCRIPTION"] = "Something unexpected happened while updating your subscription. Please try again.";
    OrderFlowErrorMessages["PROCESSING_ORDER"] = "Something unexpected happened while processing your order. Please try again.";
    OrderFlowErrorMessages["EXPIRED_CART"] = "Your cart has expired. Please restart your order.";
})(OrderFlowErrorMessages || (OrderFlowErrorMessages = {}));
export const useSession = () => {
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const pathParams = useParams();
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { balance } = useOrderFlow();
    // FIXME: In the interest of time, this fn was updated to use a Promise<any> return. While this is dirty,
    // I was not able to properly set the type of the return to anything more explicit. This'll have to do
    // for now.
    const SaveOrderFlowSession = (storeCart, model, readyToCheckout, ordersToUpdate) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        //Deep clone the store.cart object so we don't mutate it
        let cart = clone(storeCart);
        const meals = cart.meals.flatMap((meal) => {
            var _a;
            const shouldModifyRecipes = model.OrderFlowSettings.RecipeIngredientReplacementMode === RecipeIngredientReplacementMode.ENABLED;
            const recipeAssignments = (_a = meal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.filter((ra) => ra.Quantity > 0);
            if (!recipeAssignments || !recipeAssignments.length) {
                return mealToCheckoutConverter(meal, shouldModifyRecipes, null, null);
            }
            if (recipeAssignments.length === 1 && recipeAssignments[0].Quantity === meal.Quantity) {
                // If RecipeAssignment quantity matches meal quantity, just return one meal
                return mealToCheckoutConverter(meal, shouldModifyRecipes, recipeAssignments[0], null);
            }
            else {
                // Otherwise, split into multiple meals for each recipe
                let runningQuantity = meal.Quantity;
                let expandedMeals = recipeAssignments.map((assignment) => {
                    runningQuantity -= assignment.Quantity;
                    return mealToCheckoutConverter(meal, shouldModifyRecipes, assignment, assignment.Quantity);
                });
                if (runningQuantity > 0) {
                    // Create meal to account for unassigned recipes
                    expandedMeals.push(mealToCheckoutConverter(meal, shouldModifyRecipes, null, runningQuantity));
                }
                return expandedMeals;
            }
        });
        const subscriptionMeals = getSubscriptionMeals(cart, model);
        const products = (_a = cart.products) === null || _a === void 0 ? void 0 : _a.filter((product) => product.quantity > 0);
        const preferences = cart.omissions ? cart.omissions.map((omission) => omission.dislikeName) : [];
        const CategoryOmissionMappings = [];
        cart.omissions.forEach((omission) => {
            CategoryOmissionMappings.push({
                categoryId: omission.categoryId,
                dislikeId: omission.dislikeId,
                dislikeName: omission.dislikeName,
            });
        });
        const numberOfDays = [...new Set(cart.template.map((day) => day.DayOfWeek))].length;
        const shouldSaveRecipeType = model.recipeTypeSelectionMode !== RecipeTypeSelectionMode.Filter && !((_b = cart.recipeType) === null || _b === void 0 ? void 0 : _b.isAllRecipeType);
        const deliveryDates = getDeliveryDatesForOrder(cart.meals, cart.products.filter((p) => p.quantity > 0), model.DeliveryDays, moment(cart.startDate).format());
        const productsForOrderPeriod = getProductsForOrderPeriod(moment(storeCart.startDate).day(), deliveryDates.map((dd) => moment(dd).day()).sort(), storeCart.products.filter((p) => p.quantity > 0), model.Products);
        const delivScheduleDay = model.DeliverySchedule.find((dssd) => dssd.shipDayOfWeek === moment(deliveryDates[0]).day());
        let cartExpiresOn = moment().utc();
        if (delivScheduleDay) {
            let deliveryDate = moment.tz(deliveryDates[0], delivScheduleDay.ianaTimeZone);
            cartExpiresOn = deliveryDate.subtract(delivScheduleDay.CutOffDaysBefore, "days");
            cartExpiresOn = cartExpiresOn.add(delivScheduleDay.CutOffTimeInMinutes, "minutes");
            cartExpiresOn = moment.utc(cartExpiresOn);
        }
        cart.PostalCode = model.PostalCode;
        cart.LocationSku = (_c = model.Location) === null || _c === void 0 ? void 0 : _c.locationSku;
        // At this point the meals are already set so we just need grab their meal day
        const mealDaysForOrderPeriod = [...new Set(meals.map((meal) => moment(meal.MealDate).day()))];
        let checkoutData = {
            PlanSubscription: cart.isSubscription ? "Weekly Subscription" : "1 week order only",
            Preferences: preferences,
            CategoryOmissionMappings: CategoryOmissionMappings,
            NumberOfDays: numberOfDays,
            Meals: meals,
            SubscriptionMeals: subscriptionMeals,
            DeliveryDates: deliveryDates,
            OrderType: model.PostalCode ? "Delivery" : "Pickup",
            Discount: 0,
            IsSubscription: cart.isSubscription,
            SubDiscountRate: model.subscriptionDiscount,
            SubscriptionPeriod: cart.subscriptionPeriod,
            Products: productsForOrderPeriod.filter((p) => !p.isOnShortWeek || (p.isOnShortWeek && p.isRenewing)),
            ReadyToCheckout: readyToCheckout,
            startDate: cart.startDate,
            Cart: {
                CartGuid: (model.SavedCart && model.SavedCart.CartGuid) || GUID.EMPTY,
                CartJson: JSON.stringify(cart),
                ExpiresOn: cartExpiresOn,
            },
            FlowSku: (_d = pathParams === null || pathParams === void 0 ? void 0 : pathParams.sku) !== null && _d !== void 0 ? _d : model.Sku,
            ProgramSku: model.Program.Sku,
            PostalCode: cart.PostalCode,
            LocationSku: (_e = model.Location) === null || _e === void 0 ? void 0 : _e.locationSku,
            OrderMealDays: mealDaysForOrderPeriod,
            OrdersToUpdate: ordersToUpdate,
            additional_items: cart.additionalItems,
            giftCardNumber: (_f = balance === null || balance === void 0 ? void 0 : balance.giftCardPayments[0]) === null || _f === void 0 ? void 0 : _f.giftCardCode,
            PaymentMethodType: model.PaymentMethodType,
        };
        if (shouldSaveRecipeType) {
            checkoutData.RecipeTypeId = (_g = cart === null || cart === void 0 ? void 0 : cart.recipeType) === null || _g === void 0 ? void 0 : _g.recipeTypeId;
            checkoutData.RecipeTypeName = (_h = cart === null || cart === void 0 ? void 0 : cart.recipeType) === null || _h === void 0 ? void 0 : _h.recipeTypeName;
        }
        if (model.IsEditFlow) {
            setLoading(true);
            try {
                const res = yield editSubscriptionCalculate(checkoutData, readyToCheckout, model.Sku, cart.editOrderId);
                if (!res.data.success) {
                    setError(true);
                    return res;
                }
                return res;
            }
            catch (error) {
                setLoading(false);
                setError(true);
                return error;
            }
        }
        else {
            try {
                const res = yield saveOrderData(checkoutData);
                if (!res.data.success) {
                    setError(true);
                }
                return res;
            }
            catch (error) {
                setLoading(false);
                setError(true);
                return error;
            }
        }
    });
    const ContinueToCheckout = (cart, model) => __awaiter(void 0, void 0, void 0, function* () {
        const res = yield SaveOrderFlowSession(cart, model, true);
        return res.data;
    });
    /**
     * Presents a toast to the user and sends them back to the Location page
     */
    const restartOrderFlow = (sku, message, showToast = true) => {
        const toastMessage = message !== null && message !== void 0 ? message : OrderFlowErrorMessages.DEFAULT;
        if (showToast) {
            toast.push(toastMessage, {
                variant: "warning",
            });
        }
        navigate({
            pathname: `/${sku}/Location`,
        }, { state: Object.assign({}, location.state) });
    };
    /**
     * Presents a toast to the user and sends them back to Order Flow
     */
    const goBackToOrderFlow = (sku, message) => {
        const toastMessage = message !== null && message !== void 0 ? message : OrderFlowErrorMessages.DEFAULT;
        toast.push(toastMessage, {
            variant: "warning",
        });
        navigate({
            pathname: `/${sku}/OrderFlow`,
        }, { state: Object.assign({}, location.state) });
    };
    /**
     * Presents a toast to the user and re-loads the Subscriptions page
     */
    const restartEditFlow = () => {
        toast.push(OrderFlowErrorMessages.UPDATING_SUBSCRIPTION, {
            variant: "warning",
        });
        navigate({
            pathname: `/Account/Subscriptions`,
        }, { state: Object.assign({}, location.state) });
    };
    return {
        loading,
        error,
        ContinueToCheckout,
        SaveOrderFlowSession,
        restartOrderFlow,
        restartEditFlow,
        goBackToOrderFlow,
    };
};
